import {
  ChartKind,
  FunnelModeKey,
  ICohortSampleDataSummary,
  ICohortSampleTable,
  ICorrelationKind,
  ICreatorFieldsFragment,
  IDataTableColumn,
  IDataTableSliceFieldsFragment,
  IEventFrequencyCorrelationFieldsFragment,
  IExpressionColumnFieldsFragment,
  IExpressionModeXAxisFieldsFragment,
  IExpressionSliceFieldsFragment,
  IExpressionStatistics,
  IFlowFieldsFragment,
  IFunnelColumnFieldsFragment,
  IFunnelModeSliceFieldsFragment,
  IFunnelSliceFieldsFragment,
  IFunnelXAxisFieldsFragment,
  IHighlight as IGraphQLHighlight,
  IHypothesisFieldsFragment,
  IHypothesisStatsDataFieldsFragment,
  IIncidentType,
  IMeasureControl,
  IMeasureDisplayFormatFieldsFragment,
  IReadingFieldsFragment,
  IRetentionIntervalFieldsFragment,
  IRetentionSliceFieldsFragment,
  IRetentionStandardAverageFieldsFragment,
  IReturnPeriodFieldsFragment,
  ISliceNameHint,
  ISliceUserAliasFieldsFragment,
  ITopPathsSliceFieldsFragment,
  IUserAliasQuery,
  Maybe,
  MeasureDisplayFormatKind,
  Order,
  PathDirectionKey,
  PathModeKey,
  QueryType,
} from '../../utils/apollo/graphqlSchema';
import { CHART_TYPES } from './constants/chartTypes';
import { IChartLine } from './hooks/useChartLines';
import { DATA_KEY } from './constants/utils';
import { IYAxisCustomization, MaybeNil } from '../../utils/interfaces';
import { ITooltipContext } from './CustomTooltip/tooltipContextInterfaces';
import { GraphQLUserAliasId } from '../../utils/types';
import { IStateHint } from '../../utils/stateHint';
import {
  ColumnFilterConfig,
  ColumnSortConfig,
} from '../MultiGridTable/ColumnFilterWidget/types';
import { ChartViewOptions } from '../../redux/utils/chartSettings';

export interface IHighlight {
  date: string;
  text: string;
  value: string;
  isVisibleOnChart: boolean;
  columnIndex?: number;
  relatedLineName: string;
}

/**
 * ======================================================================
 * New Chart Data Model
 * ======================================================================
 */

/**
 * New Data Model: Slicable
 * Internal type: entity that can be associated to a slice
 * Analyses: Expression, Funnel, Retention, Path could be slicable (have "Breakdown" in the query builder)
 *
 */
interface ISliceable {
  key: string;
  name: string;
  sliceName: string;
  sliceKey: string;
  isComparison?: boolean;
  originalSliceKey?: string;
}

/**
 * New Data Model: Slice Columns
 * Analyses: Expression, Funnel
 *
 * Columns are data containers coming from Kobol, containing series of values. They are always associated to slice,
 * thus inherit from ISliceable
 */
export interface IMeasurableSliceColumn extends ISliceable {
  // although both IParsedExpressionColumn and IParsedFunnelColumn contain this field
  // it should not be in IParsedColumnBase because other columns (like upcoming Retention or Path)
  // may not have the concept of "measures"
  measureName: string;
  measureKey: string;
}

export interface IMeasurableComparableSliceColumn
  extends IMeasurableSliceColumn {
  // XXX this is only relevant to expression for now
  isComparison: boolean;
  isFutureComparison?: boolean;
}

/**
 * New Data Model: Measure Slice
 * Analyses: Expression, Funnel
 *
 * A slice that have associate (base) measure and variable that comprise that measure. In Funnel, measure is the
 * first step, variables are the rest of the steps
 */

export interface IHasAlias {
  hasUserAlias: boolean;
}

export interface IDataTableSliceFieldsFragmentWithComparison
  extends IDataTableSliceFieldsFragment {
  isComparison?: boolean;
}

export type ISliceFieldsFragment =
  | IExpressionSliceFieldsFragment
  | IFunnelSliceFieldsFragment
  | IFlowFieldsFragment
  | ITopPathsSliceFieldsFragment
  | IRetentionSliceFieldsFragment
  | IDataTableSliceFieldsFragmentWithComparison;

export interface IExtendedISliceFields {
  // We need to store an originalHint that
  // should not be potentially affected by aliasing(renaming)
  originalHint: ISliceFieldsFragment['hint'];
  // Keep track of whether the slice is comparison or not (can affect color, line shape etc)
  isComparison: boolean;
  originalSliceKey?: string;
}

export type IAliasableSlice = ISliceFieldsFragment &
  IHasAlias &
  IExtendedISliceFields;
export type IAliasableMeasureControl = IMeasureControl & IHasAlias;

export type IMeasureSlice = IAliasableSlice & {
  variables: number[];
};

/**
 * New Data Model: Line Meta
 *
 * "Line Meta" is auxiliary structure that allows resolving additional properties of columns, besides data like:
 * * display names
 * * references to different ways to group columns (by slice, by measure, etc)
 */

export interface ILineMetaNewBase {
  name: string;
  display: string;
  sliceKey: string;
  sliceName: string;
  type?: ILineMetaType;
}

export interface ILineMetaNewMeasurable {
  measureKey: string;
  measureName: string;
  measureLabel: string | undefined;
}

/**
 * New Data Model: Parsed Chart Data Record
 *
 * A structure to facilitate display of the data in Recharts, usually used as array item.
 * Each key is represents a data point, and the key is the unique identifier of the series.
 * For example `date` is the X-Axis series.
 * The key `isLive` is reserved as flag if the corresponding `date` record is live or not.
 */
export type IParsedChartDataRecord = Record<
  string,
  string | number | boolean | null | undefined
> & {
  isLive?: boolean;
};

/**
 * New Data Model: Alias Replacements
 *
 * A lookup by UserAliasId to identify the user-specified alias for an aliasable entity (measure, slice, slice group)
 */
export type IParsedAliasReplacements = Record<GraphQLUserAliasId, string>;

/**
 * New Data Model: Highlight
 *
 * The anomaly dot that appears if Kobol detects an anomaly in the data series.
 */

/**
 * Generic shape of a column containing highlights
 */
export interface IHighlightColumn {
  key: string;
  reading: Maybe<IReadingFieldsFragment>;
}

/**
 * The model that stores the highlights
 */
export interface IParsedHighlight extends IGraphQLHighlight {
  relatedLineName: string;
}

/**
 * New Data Model: Chart Data Interfaces
 * -------------------------------------
 * Mixins for composing different kinds of chart data that share common trait
 */

// the chart data contains slices
export interface ISliceableChartData {
  slices: ISliceFieldsFragment[];
}

// the chart data contains aliases
export interface IAliasableChartData {
  aliasReplacements: IParsedAliasReplacements;
  // to determine if aliasReplacements are saved or not
  aliasReplacementsSnapshot: IParsedAliasReplacements;
}

// the chart data contains measureControls
export interface IMeasureControlsChartData {
  measureControls: IAliasableMeasureControl[];
}

// aliasable chart data with measure controls
export type IAliasableMeasureControlsChartData = IAliasableChartData &
  IMeasureControlsChartData &
  ISliceableChartData;

// A chart data that has concept of measures: Expression and Funnel
export interface IMeasurableChartData
  extends IAliasableMeasureControlsChartData {
  slices: IMeasureSlice[];
  columns: IMeasurableSliceColumn[];
}

export interface IHighlightableChartData {
  highlights: IParsedHighlight[];
}

// A base union type for all parsed chart data
interface IParsedChartDataBase {
  queryType:
    | QueryType.Expression
    | QueryType.DataTable
    | QueryType.Funnel
    | QueryType.Retention
    | QueryType.CohortSample
    | QueryType.Path; // extend with other types here if needed
  data: IParsedChartDataRecord[];
}

/**
 * New Data Model: Expression chart data
 * -------------------------------------
 * It supports measures, slices + expression specifics like highlights, cummulative sum etc.
 * Also distinguishing primary vs comparisoon columns
 */
export type IParsedExpressionColumn = IExpressionColumnFieldsFragment &
  IMeasurableComparableSliceColumn;

export enum ILineMetaType {
  Base = 'base',
  Cumulative = 'cumulative',
  MovingAverage = 'movingAverage',
  MovingAverage30 = 'movingAverage30',
  MovingSum7 = 'movingSum7',
  MovingSum30 = 'movingSum30',
  Percent = 'percent',
}

export type ILineMetaNewExpression = Record<
  string,
  ILineMetaNewBase &
    ILineMetaNewMeasurable & {
      isResultKey?: boolean;
      parentKey?: string;
      cumulativeLineName?: string;
      cumulativeLineKey?: string;
      percentLineName?: string;
      percentLineKey?: string;
      maLineName?: string;
      maLineKey?: string;
      ma30LineName?: string;
      ma30LineKey?: string;
      ms7LineName?: string;
      ms7LineKey?: string;
      ms30LineName?: string;
      ms30LineKey?: string;
      percentChangeLineKey?: string;
    }
>;

export type IParsedChartDataExpressionSlice = IExpressionSliceFieldsFragment &
  IHasAlias &
  IExtendedISliceFields;

export interface IParsedChartDataExpression
  extends IParsedChartDataBase,
    IMeasurableChartData,
    IHighlightableChartData {
  queryType: QueryType.Expression;
  statistics?: IExpressionStatistics[];
  slices: IParsedChartDataExpressionSlice[];
  columns: IParsedExpressionColumn[];
  linesMeta: ILineMetaNewExpression;
  /**
   * Created for measure format, using the measureKey to find all data keys in chartData
   */
  dataKeysOnMeasure: Record<string, string[]>;
  xAxis: MaybeNil<IExpressionModeXAxisFieldsFragment>;
  dates: string[];
  /**
   * This dictionary is used to precalculate the negative value of each column.
   * For example, if the displayed column has negative value, we need to adjust the bounds of the Y-Axis
   * from [0, 'auto'] to ['-auto', 'auto] to show the negative value unless user has applied custom Y-Axis bounds.
   */
  negativeValueDictionary: Record<string, boolean>;
}

/**
 * New Data Model: Data TAble chart data
 * -------------------------------------
 * It supports measures, slices + funnel specifics (TBD: perhaps percents)
 */

export type IParsedDataTableColumn = IDataTableColumn & ISliceable;

export type IParsedDataTableSlice = IDataTableSliceFieldsFragment &
  IHasAlias &
  IExtendedISliceFields;

export interface IParsedChartDataDataTable
  extends IParsedChartDataBase,
    IAliasableMeasureControlsChartData,
    IHighlightableChartData {
  queryType: QueryType.DataTable;
  slices: Array<IParsedDataTableSlice>;
  columns: IParsedDataTableColumn[];
  measureControls: IAliasableMeasureControl[];
}

/**
 * New Data Model: Funnel chart data
 * -------------------------------------
 * It supports measures, slices + funnel specifics (TBD: perhaps percents)
 */

export type ILineMetaNewFunnel = Record<
  string,
  ILineMetaNewBase &
    ILineMetaNewMeasurable & {
      funnelModeSlice?: MaybeNil<IParsedFunnelModeSlice>;
    }
>;

interface IMeasureRange {
  firstMeasureKey: string;
  lastMeasureKey: string;
}

export type IParsedFunnelColumn = IFunnelColumnFieldsFragment &
  IMeasurableSliceColumn;
export type IParsedFunnelModeSlice = IFunnelModeSliceFieldsFragment &
  IMeasureRange;

export const FUNNEL_SINGLE_DAY_X_AXIS_KEY = 'sliceName' as const;
export const FUNNEL_SLICE_KEY_PROP_NAME = 'sliceKey' as const;
export const FUNNEL_FREQUENCY_X_AXIS_KEY = 'frequency' as const;
export const FUNNEL_TIME_TO_CONVERT_X_AXIS_KEY = 'timeToConvert' as const;
export type FunnelXAxisKey =
  | typeof DATA_KEY
  | typeof FUNNEL_SINGLE_DAY_X_AXIS_KEY
  | typeof FUNNEL_FREQUENCY_X_AXIS_KEY
  | typeof FUNNEL_TIME_TO_CONVERT_X_AXIS_KEY;

export type IRange = [number, number];

export type IParsedChartDataFunnelSlice = IFunnelSliceFieldsFragment &
  IAliasableSlice;

export interface IParsedChartDataFunnel
  extends IParsedChartDataBase,
    IMeasurableChartData,
    IHighlightableChartData {
  queryType: QueryType.Funnel;
  slices: IParsedChartDataFunnelSlice[];
  columns: IParsedFunnelColumn[];
  linesMeta: ILineMetaNewFunnel;
  xAxisKey: FunnelXAxisKey;
  dates: string[];
  xAxis: Maybe<IFunnelXAxisFieldsFragment>;
  funnelModeSlices: IParsedFunnelModeSlice[];
  mode: FunnelModeKey;
  xAxisRange?: IRange;
  showInReportState: IStateHint;
}

/**
 * New Data Model: Retention chart data
 */

export type ILineMetaNewRetention = Record<
  string,
  ILineMetaNewBase &
    ILineMetaNewMeasurable & {
      percentLineKey: string;
    }
>;

/**
 * RETENTION DATA MODEL TERMINOLOGY
 * --------------------------------
 * - INTERVAL: a range between 2 dates (5/31 - 6/1)
 *  -- START INTERVAL: an interval of time in which subjects performed the start event.
 *     defines a cohort of users. E.g. (5/31 - 6/5)
 *  -- RETURN INTERVAL: an interval of time in which subjects performed the end event E.g. (6/5 - 6/12)
 * - PERIOD: length of time - usually a number and a unit (1 day, 1 week, 2 weeks).
 *  -- RETURN PERIOD: a length of time when users performed the return event, relative to the start cohort
 *             (and its start interval)
 *     example: for cohort 5/31 - 6/5, the return interval 6/5 - 6/12 is return period "Week 1"
 *              for cohort 5/31 - 6/5, the return interval 6/12 - 6/21 is return period "Week 2"
 *              for cohort 5/31 - 6/5, the return interval 6/21 - 6/28 is return period "Week 3"
 *              for cohort 6/5 - 6/12, the return interval 6/12 - 6/21 is return period "Week 1"
 *              for cohort 6/5 - 6/12, the return interval 6/21 - 6/28 is return period "Week 2"
 *    "Week 2" for cohort 5/31 - 6/5 is 2 weeks after the end the interval (6/5)
 * - CELL - a single cell in the table
 * - HEADER: a special cell *above* each column or *before* each row
 *  -- ROW HEADER: the first cell in each row of the table, before all other cells in the same row (left to right).
 *     Effectively the first column
 *  -- COLUMN HEADER: the first cell in each colum (top to bottom). Effectively the first row
 * - STANDARD RETENTION - referred simply as "Retention" in the UI
 * - OVER TIME RETENTION - referred as "Retention Over Time" in the UI
 */

/**
 * ===== Retention Cells ====
 */

/**
 * Start Cells of the Standard Retention. Number of subjects which performed the start event and the interval of time
 * they performed it
 */
export interface IRetentionStartCell {
  live: boolean;
  startCount: number;
  startInterval: IRetentionIntervalFieldsFragment;
  actualInterval: IRetentionIntervalFieldsFragment;
}

/**
 * Return Cells of the Standard or Over Time Retention. Number of subjects which performed the return event
 * For convenience carry the startInterval of the cohort (e.g. 5/31 - 6/5), return interval (e.g. 6/5 - 6/12) and the
 * return period (Week 1)
 */
export interface IRetentionReturnCell {
  retentionCount: number;
  retentionPercent: MaybeNil<number>;
  churnCount: number;
  churnPercent: MaybeNil<number>;
  live: boolean;
  startInterval: IRetentionIntervalFieldsFragment;
  actualInterval: IRetentionIntervalFieldsFragment;
  returnPeriod: IReturnPeriodFieldsFragment;
  returnInterval: IRetentionIntervalFieldsFragment;
  __typename: 'IRetentionReturnCell';
}

/**
 * Start Cells of the average row of the Standard Retention. Since it is an average across cohorts it doesn't have
 * start interval compared to IRetentionStartCell
 */
export interface IRetentionAverageStartCell {
  startCount: number;
}

/**
 * Return Cells of the average row of the Standard Retention. Since it is an average across cohorts it doesn't have
 * intervals, just the periods
 */
export interface IRetentionAverageReturnCell {
  retentionCount: number;
  retentionPercent: MaybeNil<number>;
  churnCount: number;
  churnPercent: MaybeNil<number>;
  returnPeriod: IReturnPeriodFieldsFragment;
  __typename: 'IRetentionAverageReturnCell';
}

/**
 * ===== Retention Headers ====
 */
export enum RetentionHeaderType {
  Cohort,
  CohortAverage,
  ReturnPeriod,
}

/**
 * Header for the cohort which performed the start event. It is a ROW header
 * in Standard Retention or COLUMN header of the Over Time Retention
 * index is used to perform sorting. Since it is a cohort header, it contains
 * the start interval (e.g. 5/31 - 6/5) when the subjects performed the start
 * event
 */
export interface IRetentionCohortHeader {
  type: RetentionHeaderType.Cohort;
  key: string;
  index: number;
  startInterval: IRetentionIntervalFieldsFragment;
}

/**
 * Header for return cells It is a COLUMN header in Standard Retention or
 * ROW header of the Over Time Retention. It contains a RETURN PERIOD (e.g. Week 2)
 */
export interface IRetentionReturnPeriodHeader {
  type: RetentionHeaderType.ReturnPeriod;
  index: number;
  key: string;
  returnPeriod: IReturnPeriodFieldsFragment;
}

/**
 * Header for the average row in Standard Retention. It is only used as ROW
 * header as Over Time retention doesn't support average
 */
export interface IRetentionCohortAverageHeader {
  type: RetentionHeaderType.CohortAverage;
  index: number;
  key: string;
  average: IRetentionStandardAverageFieldsFragment;
}

/**
 * Union of headers possible in Standard Retention
 */
export type IRetentionStandardRowHeader =
  | IRetentionCohortHeader
  | IRetentionCohortAverageHeader;

/**
 * Union of all headers
 */
export type IRetentionHeader =
  | IRetentionCohortHeader
  | IRetentionCohortAverageHeader
  | IRetentionReturnPeriodHeader;

/**
 * ==== Retention Rows ===
 *
 * Represent a single row in the retention table
 */

export enum RetentionStandardRowType {
  Cohort,
  Average,
}

/**
 * Standard Retention row: contains start cohort cell, and list of return
 * cells. Contains a reference to the cohort ROW header for convenience
 */
export interface IRetentionStandardCohortRow {
  type: RetentionStandardRowType.Cohort;
  key: string;
  sliceKey: string;
  header: IRetentionCohortHeader;
  start: IRetentionStartCell;
  returns: IRetentionReturnCell[];
}

/**
 * Standard Retention Average row: contains start average cell, and list of return
 * average cells. Contains a reference to the average ROW header for convenience
 */
export interface IRetentionStandardAverageRow {
  type: RetentionStandardRowType.Average;
  key: string;
  sliceKey: string;
  header: IRetentionCohortAverageHeader;
  start: IRetentionAverageStartCell;
  returns: IRetentionAverageReturnCell[];
}

/**
 * Union of the possible rows in the Standard Retention
 */
export type IRetentionStandardRow =
  | IRetentionStandardCohortRow
  | IRetentionStandardAverageRow;

/**
 * A row in Over Time Retention. It contains only return cells as we don't
 * display start event subject counts. Contains a reference to Return Period
 * as ROW header
 */
export interface IRetentionOverTimeRow {
  key: string;
  sliceKey: string;
  header: IRetentionReturnPeriodHeader;
  returns: IRetentionReturnCell[];
}

/**
 * ==== Retention Table ===
 *
 * Represent the entire retention table
 */

export enum RetentionTableType {
  Standard,
  OverTime,
}

export interface IRetentionRange {
  min: number;
  max: number;
}
/**
 * Standard Retention table:
 * - column headers are return periods (Week 1, Week 2, etc.)
 * - row headers are cohorts (5/31 - 6/5)
 * - uses standard rows
 */
export interface IRetentionStandardTable {
  type: RetentionTableType.Standard;
  rowHeaders: IRetentionStandardRowHeader[];
  columnHeaders: IRetentionReturnPeriodHeader[];
  rows: IRetentionStandardRow[];
  retentionRange: IRetentionRange;
  churnRange: IRetentionRange;
}

/**
 * Over Time Retention table:
 * - column headers are cohorts (5/31 - 6/5)
 * - row headers are return periods (Week 1, Week 2, etc.)
 * - uses over time rows
 */

export interface IRetentionOverTimeTable {
  type: RetentionTableType.OverTime;
  rowHeaders: IRetentionReturnPeriodHeader[];
  columnHeaders: IRetentionCohortHeader[];
  rows: IRetentionOverTimeRow[];
  retentionRange: IRetentionRange;
  churnRange: IRetentionRange;
}

/**
 * Union of all possible tables
 */
export type IRetentionTable = IRetentionStandardTable | IRetentionOverTimeTable;

export interface IParsedChartDataRetention
  extends IParsedChartDataBase,
    ISliceableChartData,
    IAliasableChartData {
  queryType: QueryType.Retention;
  slices: Array<IRetentionSliceFieldsFragment & IAliasableSlice>;
  table: IRetentionTable;
  linesMeta: ILineMetaNewRetention;
}

/**
 * New Data Model: Path chart data
 */

interface IParsedChartDataPathBase
  extends IParsedChartDataBase,
    ISliceableChartData,
    IAliasableChartData {
  queryType: QueryType.Path;
  mode: PathModeKey.Sankey | PathModeKey.TopPaths;
  slices: Array<ISliceFieldsFragment & IAliasableSlice>;
  canCreateFunnel: IStateHint;
}

export interface IParsedChartDataPathSankey extends IParsedChartDataPathBase {
  mode: PathModeKey.Sankey;
  slices: Array<IFlowFieldsFragment & IAliasableSlice>;
  directionKey: PathDirectionKey;
  maxSeq: number;
  eventsPerStep: number;
}

export interface IParsedChartDataPathTopPaths extends IParsedChartDataPathBase {
  mode: PathModeKey.TopPaths;
  slices: Array<ITopPathsSliceFieldsFragment & IAliasableSlice>;
  base: MaybeNil<ITopPathsSliceFieldsFragment & IAliasableSlice>;
  creator: ICreatorFieldsFragment;
  transientAnalysisIds: string[];
}

export type IParsedChartDataPath =
  | IParsedChartDataPathSankey
  | IParsedChartDataPathTopPaths;

export interface IParsedChartDataCohortSample {
  queryType: QueryType.CohortSample;
  data: {
    subjectName: string;
    tables: ICohortSampleTable[];
    summary: Maybe<Pick<ICohortSampleDataSummary, 'number' | 'text'>>;
  };
}

export interface IPredictionCorrelationData {
  strongestFrequencyIndex: number;
  strongestValues: Record<string, number>;
  predictionChartData: Record<string, any[]>;
  eventFrequencyCorrelations: IEventFrequencyCorrelationFieldsFragment[];
  columnIndex: number;
}

export interface IParsedChartDataPrediction {
  queryType: QueryType.Prediction;
  data: {
    correlationKinds: ICorrelationKind[];
    events: string[];
    selectedValue: string;
    correlationData: Record<string, IPredictionCorrelationData[]>;
  };
}

export interface IParsedChartDataSignificance {
  queryType: QueryType.Significance;
  data: ISignificanceTableData | null;
}

/**
 * New Data Model: The tagged union type for all chart data we support
 * -------------------------------------
 */

export type IParsedChartData =
  | IParsedChartDataExpression
  | IParsedChartDataDataTable
  | IParsedChartDataFunnel
  | IParsedChartDataRetention
  | IParsedChartDataPath
  | IParsedChartDataCohortSample
  | IParsedChartDataPrediction
  | IParsedChartDataSignificance;

/**
 * -------------------------------------
 * End of New Data Model
 * -------------------------------------
 */

/**
 * -------------------------------------
 * Chart Settings Model
 * -------------------------------------
 */

export interface IDisplayedSliceSetting {
  key: string;
  isVisible: boolean;
  color?: string;
}

export interface IDisplayedMeasureSetting {
  isVisible: boolean;
  format: Maybe<IMeasureDisplayFormatFieldsFragment>;
  key: string;
  yAxisPosition?: YAxisPosition;
  color?: string;
}

export interface IDisplayedSlice extends IDisplayedSliceSetting, IHasAlias {
  name: string;
  rank: number | null;
  hint: ISliceNameHint[];
  userAlias: ISliceUserAliasFieldsFragment;
  originalHint: ISliceNameHint[];
  isComparison: boolean;
  originalSliceKey?: string;
}

export interface IDisplayedMeasure extends IDisplayedMeasureSetting, IHasAlias {
  name: string;
  charts: ChartKind[];
  userAlias: IUserAliasQuery;
  label: string;
  isResultKey?: boolean;
  yAxisPosition: YAxisPosition;
}

export interface IDisplayedHighlight
  extends Omit<IParsedHighlight, 'relatedLineName'> {
  relatedLine: IChartLine;
}

/** @deprecated use ICentralChartYAxisAssignment instead */
export interface IYAxisSetting {
  yAxisCustomization: MaybeNil<IYAxisCustomization>;
  isTruncateYAxis: boolean;
}

export interface IYAxisSettings {
  [YAxisPosition.LEFT]: IYAxisSetting;
  [YAxisPosition.RIGHT]: IYAxisSetting;
}

export enum OthersConfigAction {
  COMBINE = 'COMBINE',
  SEPARATE = 'SEPARATE',
}

export interface IPathNodeSetting {
  name: string;
  othersConfig: Array<{
    seq: number;
    action: OthersConfigAction;
  }>;
}

export interface IGeoMapTransform {
  x: number;
  y: number;
  // k is scale of the map
  k: number;
}

export interface IGeoMapConfig extends IGeoMapTransform {
  isHeatmap: boolean;
  displayedMeasureKey: string | null;
}

export interface ICentralYAxisSettings {
  isApplyYAxisFormat: boolean;
  format: {
    kind: MeasureDisplayFormatKind;
    decimalPlace: number;
  };
  isTruncate: boolean;
  isVisible: boolean;
  label: Maybe<string>;
  bounds: MaybeNil<{
    min: number | null;
    max: number | null;
  }>;
}
export interface ICentralXAxisSettings {
  isVisible: boolean;
  label: Maybe<string>;
}
export interface IAxisSettings {
  yAxisSettings: Record<YAxisPosition, ICentralYAxisSettings>;
  xAxisSettings: ICentralXAxisSettings;
}

export interface IChartSettings {
  chartType: CHART_TYPES; // Expression, Funnel, Retention
  displayedSliceSettings: IDisplayedSliceSetting[]; // Expression, Funnel, Retention
  displayedMeasureSettings: IDisplayedMeasureSetting[]; // Expression, Funnel
  singleDayLabel: string; // Expression, Retention
  isConversionView: boolean; // Funnel
  isShowFunnelConversionLabels: boolean; // Funnel
  isShowFunnelBounceLabels: boolean; // Funnel
  isFunnelClusterMode: boolean; // Funnel
  isFunnelRelativeLabels: boolean; // Funnel
  // Use tyoe is going to differentiate with global number format
  // chartNumberType is used in Retention, Stacked Column Chart, Area Chart
  chartNumberType: ChartNumberType;
  isChurn: boolean; // Retention
  sort: ChartSortEnum; // Expression, Funnel, Retention
  displaySeq: number; // Path
  pathEventsPerStep: number; // Path
  /** @deprecated use axisSettings instead */
  yAxisSettings: IYAxisSettings;
  pathNodeSettings: IPathNodeSetting[];
  expressionSettings: IExpressionSettings | null; // Expression
  tableSettings: ITableSettings;
  pathObservedNodeId: MaybeNil<string>;
  topBreakdownsValue: number;
  geoMapConfig: IGeoMapConfig;
  activeIncidentTypes: Record<IIncidentType['id'], boolean>;
  axisSettings: IAxisSettings;
}

/**
 * Defines settings that used to be in IChartSettings but they are replaced with
 * something else.
 *
 * IMPORTANT: This is not _old_ (pre V2) chart settings. These are properties
 * which are deprecated _after_ introduction ov V2 chart settings.
 *
 * For extra safety, all properties here should be defined as optional and
 * nullable type to enforce runtime check in the migration code
 */
export interface ILegacyChartSettings {
  /**
   * Replaced by IDisplayedMeasureSetting['yAxisPosition']
   */
  dualAxesConfig?: MaybeNil<YAxisPosition[]>;
}

export interface IBuildLineValue {
  lineName: string;
  lineValues: any;
  linesMeta: any;
  isRetentionTooltip?: boolean;
  forceBothValues?: boolean;
  isRetentionPercent?: boolean;
  measureFormat: Maybe<IMeasureDisplayFormatFieldsFragment>;
  tooltipContext?: ITooltipContext;
  activeDot?: IDot;
  // special case for Chart Badge, Chart Badge use raw data directly, therefore cannot ignore calculation in tooltip
  ignoreCalculation?: boolean;
  isFunnelRelativeLabels?: boolean;
}

export interface IDot {
  lineName: string;
  lineColor: string;
  value: number;
  cy: number;
  cx: number;
  date: string;
  isHighlight?: boolean;
  highlightText?: string;
}

export enum ExpressionPostfixOnColumnName {
  cumulativeKey = '-cumulative',
  cumulativeName = ' - Cumulative Sum',
  percentKey = '-percent',
  percentName = ' - Percent',
  percentsPreviousValueKey = '-percentsPreviousValue',
  maKey = '-ma',
  maName = ' - Rolling Average 7',
  ma30Key = '-ma30',
  ma30Name = ' - Rolling Average 30',
  ms7Key = '-ms7',
  ms7Name = ' - Rolling Sum 7',
  ms30Key = '-ms30',
  ms30Name = ' - Rolling Sum 30',
}

export enum FunnelPostfixOnColumnName {
  percentKey = '-percent',
  bounceKey = '-bounce-value',
  bouncePercentKey = '-bounce-percent',
}

export enum RetentionPostfixOnColumnName {
  percentKey = '-percent',
}

export enum ExpressionSettingsLineType {
  Ma = 'ma',
  Ma30 = 'ma30',
  Ms7 = 'ms7',
  Ms30 = 'ms30',
  CumulativeSum = 'cumulativeSum',
}

export interface IExpressionSettings {
  showMeasure: boolean;
  lineType: ExpressionSettingsLineType | '';
  showCompare: boolean;
}

export enum TableSortType {
  NUMERICAL = 'numerical',
  ALPHABETICAL = 'alphabetical',
}

/*
  an ordering of table columns that will display
  data from kobol based on user defined order.
*/
export type ColumnMapping = number[];

export interface ITableSettings {
  chartView: ChartViewOptions;
  columnWidths: number[] | null;
  searchTerm: string;
  sortColumnIndex: number | null;
  sortDirection?: Order;
  sortType?: TableSortType;
  columnMapping: ColumnMapping | null;
  showHorizontalBars: boolean;
  filterConfig: ColumnFilterConfig | null;
  sortConfig: ColumnSortConfig | null;
  showPercentDelta?: boolean;
}

/** Each line/bar can be assigned to one of these axes */
export enum YAxisPosition {
  LEFT = 'LEFT',
  RIGHT = 'RIGHT',
}

export enum ChartSortEnum {
  NONE = 'NONE',
  ASC = 'ASC',
  DESC = 'DESC',
}

/**
 * Chart display number type: Number or Percentage
 * This is used for Retention Charts / Stacked Column Chart / Area Chart
 * The change of number type will affect the display of the chart
 */
export enum ChartNumberType {
  Number = 'NUMBER',
  Percentage = 'PERCENTAGE',
}

export type ISignificanceVariantPerfResult = 'negative' | 'positive';

export type ISignificanceVariantStats = Pick<
  IHypothesisFieldsFragment,
  'variant' | 'mean' | 'standardDeviation'
> &
  Pick<
    IHypothesisStatsDataFieldsFragment,
    | 'standardError'
    | 'pValue'
    | 'statisticalPower'
    | 'confidenceInterval'
    | 'testScore'
    | 'statisticalSignificance'
    | 'lift'
    | 'delta'
  > & {
    performanceResult: ISignificanceVariantPerfResult;
    sampleSize: string;
    executedSimulations: string;
  };

export type ISignificanceControlStats = Pick<
  IHypothesisFieldsFragment,
  'variant' | 'mean' | 'standardDeviation'
> & {
  sampleSize: string;
};

export interface ISignificanceTableData {
  metricName: string;
  control: ISignificanceControlStats | null;
  variantA: ISignificanceVariantStats | null;
  variantB: ISignificanceVariantStats | null;
  leadingVariant?: 'variantA' | 'variantB';
  // It's possible that chart data is not empty, only some part of it is.
  // In order to help user to troubleshoot the reason - we will handle error
  // and show it on UI instead of SignificancePerformanceChart
  error?: string;
}
